/**
 * Partner portal API — the corporate's own view of their campaigns.
 *
 * Login is un-authed; everything else goes through `partnerFetch` (partner JWT,
 * scoped to the caller's partner; 401 bounces to /partner/login).
 */
import { ApiError, type CampaignRead } from './api';
import {
  getPartnerToken,
  partnerFetch,
  setPartnerSession,
} from './partnerAuth';

export type { CampaignRead };

export interface PartnerLoginResponse {
  access_token: string;
  partner_id: string;
}

/** Authenticate a partner user and persist the partner session. */
export async function partnerLogin(
  email: string,
  password: string,
): Promise<PartnerLoginResponse> {
  let resp: Response;
  try {
    resp = await fetch('/api/v1/auth/partner/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  } catch (e) {
    throw new ApiError(
      'We could not reach the server. Please check your connection and try again.',
      0,
      e,
    );
  }
  if (!resp.ok) {
    const message =
      resp.status === 401
        ? 'Invalid email or password.'
        : 'Sign-in failed. Please try again.';
    throw new ApiError(message, resp.status);
  }
  const data = (await resp.json()) as PartnerLoginResponse;
  setPartnerSession(data.access_token, data.partner_id);
  return data;
}

/** List the partner's OWN campaigns. */
export function listMyCampaigns(): Promise<CampaignRead[]> {
  return partnerFetch<CampaignRead[]>(
    '/api/v1/partner/campaigns',
    { method: 'GET' },
    'Could not load your campaigns.',
  );
}

/** Read one of the partner's OWN campaigns. */
export function getMyCampaign(id: string): Promise<CampaignRead> {
  return partnerFetch<CampaignRead>(
    `/api/v1/partner/campaigns/${encodeURIComponent(id)}`,
    { method: 'GET' },
    'Could not load this campaign.',
  );
}

/** Campaign tracking funnel (scans → entries → leads), partner-scoped.
 *  Mirrors the backend CampaignFunnelResponse (selfserve funnel endpoint). */
export interface CampaignFunnel {
  campaign_id?: string;
  competition_id?: string | null;
  status?: string;
  funnel?: Record<string, number>;
  conversion_rate?: number | null;
  traffic_sources?: Record<string, number>;
  winner?: { verified?: boolean; delivered?: boolean; delivery_video_url?: string | null };
  leads?: number | null;
  as_of?: string | null;
  stale?: boolean;
  degraded?: boolean;
  [k: string]: unknown;
}

/** Read the live tracking funnel for one of the partner's campaigns. */
export function getMyCampaignFunnel(id: string): Promise<CampaignFunnel> {
  return partnerFetch<CampaignFunnel>(
    `/api/v1/partner/campaigns/${encodeURIComponent(id)}/funnel`,
    { method: 'GET' },
    'Could not load the campaign tracking.',
  );
}

// --- consented contactable leads (partner-leads-selfserve) -------------------
//
// The partner-scoped, consent-gated lead export. Mirrors the staff export but is
// HARD-SCOPED server-side to the caller's own partner: only THIS campaign's
// consented, un-withdrawn leads are returned, with contact PII (name/email/phone)
// decrypted server-side. A campaign owned by another partner 404s. Every access
// is audited with the partner user as actor.

/** One consented lead — opaque token + consent provenance + (consented) PII. */
export interface PartnerLeadItem {
  lead_id: string;
  participant_token: string;
  source: string;
  captured_at: string;
  consent_text_version: string;
  consent_scope: string;
  consent_at?: string | null;
  // Decrypted contact PII — null when no contact details were captured.
  name?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface PartnerLeadsResponse {
  campaign_id: string;
  partner_id: string;
  count: number;
  leads: PartnerLeadItem[];
}

/** Fetch THIS campaign's consented contactable leads (incl. decrypted PII). */
export function getMyCampaignLeads(id: string): Promise<PartnerLeadsResponse> {
  return partnerFetch<PartnerLeadsResponse>(
    `/api/v1/partner/campaigns/${encodeURIComponent(id)}/leads`,
    { method: 'GET' },
    'Could not load your consented leads for this campaign.',
  );
}

/**
 * Download THIS campaign's consented-leads CSV, with the partner Bearer token
 * attached (same partner-scope + consent gate + audit as the JSON view). Triggers
 * a browser download via an object URL. Throws ApiError on a non-2xx response.
 */
export async function downloadMyCampaignLeadsCsv(id: string): Promise<void> {
  const url = `/api/v1/partner/campaigns/${encodeURIComponent(id)}/leads.csv`;
  const token = getPartnerToken();
  const headers = new Headers();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  let resp: Response;
  try {
    resp = await fetch(url, { method: 'GET', headers });
  } catch (e) {
    throw new ApiError(
      'We could not reach the server. Please check your connection and try again.',
      0,
      e,
    );
  }
  if (resp.status === 401) {
    // Mirror partnerFetch: a stale token bounces to the partner login.
    if (typeof window !== 'undefined') window.location.assign('/partner/login');
    throw new ApiError('Your session has expired. Please sign in again.', 401);
  }
  if (!resp.ok) {
    throw new ApiError('Could not download your leads CSV.', resp.status);
  }

  const blob = await resp.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `leads-${id}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

// --- branded flyer (corp-qr-flyer story 2) -----------------------------------
//
// The corporate sponsor's branded, shareable flyer (prizes' competition flyer +
// this campaign's tracking QR) — a richer replacement for the plain QR. Corporate
// proxies prizes behind the partner JWT, so these are partner-scoped + same-origin.

/** One branded flyer for a campaign. `download_path` is the corporate proxy. */
export interface PartnerFlyerItem {
  flyer_id: string;
  thumbnail_url: string | null;
  download_path: string;
}

/** List this campaign's branded flyers (empty when none / not linked / prizes down). */
export function getMyCampaignFlyers(id: string): Promise<{ flyers: PartnerFlyerItem[] }> {
  return partnerFetch<{ flyers: PartnerFlyerItem[] }>(
    `/api/v1/partner/campaigns/${encodeURIComponent(id)}/flyers`,
    { method: 'GET' },
    'Could not load your campaign flyers.',
  );
}

/**
 * Fetch a branded flyer JPEG (partner Bearer attached) and return an object URL —
 * the endpoint is auth-gated, so a bare <img src> can't load it. The caller owns
 * the URL and must URL.revokeObjectURL() it when done.
 */
export async function fetchMyCampaignFlyerUrl(downloadPath: string): Promise<string> {
  const token = getPartnerToken();
  const headers = new Headers();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  let resp: Response;
  try {
    resp = await fetch(downloadPath, { method: 'GET', headers });
  } catch (e) {
    throw new ApiError('We could not reach the server. Please try again.', 0, e);
  }
  if (resp.status === 401) {
    if (typeof window !== 'undefined') window.location.assign('/partner/login');
    throw new ApiError('Your session has expired. Please sign in again.', 401);
  }
  if (!resp.ok) {
    throw new ApiError('Could not load the flyer.', resp.status);
  }
  return URL.createObjectURL(await resp.blob());
}

/** Trigger a browser download of the given object URL as a .jpg. */
export function downloadObjectUrl(objectUrl: string, filename: string): void {
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
